import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useGetPublicContestBySlugQuery } from "../../features/api/appApi";

const errorToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (el) => {
    el.addEventListener("mouseenter", Swal.stopTimer);
    el.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

const showErrorToast = (message) => errorToast.fire({ icon: "error", title: message });

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : "https://api.gubcpc.com");

const panelClass = "home-card-strong p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8";
const sectionTitleClass = "home-title mt-4 text-2xl font-bold sm:text-4xl text-[var(--text)]";
const bodyTextClass = "text-sm sm:text-base home-muted leading-relaxed";
const primaryButtonClass = "home-btn-primary px-6 py-2.5 text-sm font-semibold sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const secondaryButtonClass = "home-btn-secondary px-6 py-2.5 text-sm font-medium sm:px-8 sm:py-3 sm:text-base cursor-pointer";
const inputClass = "w-full rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3 text-sm text-[var(--text)] outline-none transition-all duration-300 placeholder:text-[#94A3B8]/60 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)]";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[+]?[\d\s-]{7,}$/;
const tshirtSizes = ["S", "M", "L", "XL", "XXL", "XXXL"];

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-BD", { dateStyle: "long" }).format(date);
}

function blankParticipant() {
  return { name: "", studentId: "", email: "", phone: "", department: "", tshirtSize: "" };
}

export default function ContestRegisterPage() {
  const { slug } = useParams();
  const { data: contest, isLoading, isError } = useGetPublicContestBySlugQuery(slug);

  const [teamName, setTeamName] = useState("");
  const [reference, setReference] = useState("");
  const [participants, setParticipants] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentNumber, setPaymentNumber] = useState("");
  const [paymentTransactionId, setPaymentTransactionId] = useState("");
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successResult, setSuccessResult] = useState(null);

  useEffect(() => {
    if (!contest) return;
    setParticipants((prev) => {
      if (prev.length > 0) return prev;
      const count = contest.type === "team" ? contest.minTeamSize || 2 : 1;
      return Array.from({ length: count }, blankParticipant);
    });
  }, [contest]);

  const updateParticipant = (index, field, value) => {
    setParticipants((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
    const key = `p${index}-${field}`;
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const addMember = () => {
    if (!contest || contest.type !== "team") return;
    setParticipants((prev) =>
      prev.length >= contest.maxTeamSize ? prev : [...prev, blankParticipant()],
    );
  };

  const removeMember = (index) => {
    if (!contest) return;
    setParticipants((prev) =>
      prev.length <= contest.minTeamSize ? prev : prev.filter((_, i) => i !== index),
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (contest.type === "team" && !teamName.trim()) {
      newErrors.teamName = "Team name is required.";
    }

    participants.forEach((p, i) => {
      if (!p.name.trim()) newErrors[`p${i}-name`] = "Name is required.";
      if (!p.studentId.trim()) newErrors[`p${i}-studentId`] = "Student ID is required.";
      if (!p.department.trim()) newErrors[`p${i}-department`] = "Department is required.";
      if (contest.requiresTshirtSize && !p.tshirtSize.trim()) {
        newErrors[`p${i}-tshirtSize`] = "T-shirt size is required.";
      }
      if (!p.email.trim()) {
        newErrors[`p${i}-email`] = "Email is required.";
      } else if (!emailRegex.test(p.email.trim())) {
        newErrors[`p${i}-email`] = "Enter a valid email address.";
      }
      if (!p.phone.trim()) {
        newErrors[`p${i}-phone`] = "Phone number is required.";
      } else if (!phoneRegex.test(p.phone.trim())) {
        newErrors[`p${i}-phone`] = "Enter a valid phone number.";
      }
    });

    if (contest.type === "team") {
      if (participants.length < contest.minTeamSize || participants.length > contest.maxTeamSize) {
        newErrors.teamSize = `Team must have between ${contest.minTeamSize} and ${contest.maxTeamSize} members.`;
      }
      for (let i = 0; i < participants.length; i += 1) {
        for (let j = i + 1; j < participants.length; j += 1) {
          const a = participants[i];
          const b = participants[j];
          if (a.email.trim() && a.email.trim().toLowerCase() === b.email.trim().toLowerCase()) {
            newErrors[`p${j}-email`] = "This email is already used by another member in this form.";
          }
          if (a.studentId.trim() && a.studentId.trim() === b.studentId.trim()) {
            newErrors[`p${j}-studentId`] = "This student ID is already used by another member in this form.";
          }
          if (a.phone.trim() && a.phone.trim() === b.phone.trim()) {
            newErrors[`p${j}-phone`] = "This phone number is already used by another member in this form.";
          }
        }
      }
    }

    if (contest.requiresPayment) {
      if (!paymentMethod.trim()) newErrors.paymentMethod = "Payment method is required.";
      if (!paymentNumber.trim()) newErrors.paymentNumber = "Payment sender number is required.";
      if (!paymentTransactionId.trim()) newErrors.paymentTransactionId = "Transaction ID is required.";
    }

    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!contest || !contest.isRegistrationOpen) return;

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setNotice("Please fix the errors below before submitting.");
      showErrorToast("Please fix the errors below before submitting.");
      return;
    }

    setErrors({});
    setNotice("");
    setSubmitting(true);

    const body = {
      participants: participants.map((p) => ({
        name: p.name.trim(),
        studentId: p.studentId.trim(),
        email: p.email.trim(),
        phone: p.phone.trim(),
        department: p.department.trim(),
        tshirtSize: p.tshirtSize.trim(),
      })),
    };
    if (contest.type === "team") body.teamName = teamName.trim();
    if (reference.trim()) body.reference = reference.trim();
    if (contest.requiresPayment) {
      body.paymentMethod = paymentMethod.trim();
      body.paymentNumber = paymentNumber.trim();
      body.paymentTransactionId = paymentTransactionId.trim();
    }

    try {
      const res = await fetch(`${API_BASE_URL}/student/contests/${slug}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": contest.apiKey,
        },
        body: JSON.stringify(body),
      });

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.message || "Failed to submit registration.");
      }

      setSuccessResult(resData.registration || {});
    } catch (err) {
      const message = err.message || "An error occurred during submission.";
      setNotice(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <main className="home-shell min-h-screen pt-24 pb-12">
        <div className="home-container px-4 sm:px-6">
          <div className="home-card p-8 text-center">
            <p className={bodyTextClass}>Loading contest...</p>
          </div>
        </div>
      </main>
    );
  }

  if (isError || !contest) {
    return (
      <main className="home-shell min-h-screen pt-24 pb-12">
        <div className="home-container px-4 sm:px-6">
          <section className={`${panelClass} max-w-2xl mx-auto text-center`}>
            <span className="section-kicker mb-3">CONTEST NOT FOUND</span>
            <h1 className={sectionTitleClass}>We couldn't find this contest</h1>
            <p className={`mt-4 ${bodyTextClass}`}>
              This contest may not exist, may still be a draft, or may have been removed.
            </p>
            <Link to="/contests" className={`${primaryButtonClass} mt-6 inline-flex`}>
              Back to Contests
            </Link>
          </section>
        </div>
      </main>
    );
  }

  const deadline = formatDate(contest.registrationDeadlineAt);
  const contestDate = formatDate(contest.contestDate);

  if (successResult) {
    return (
      <main className="home-shell min-h-screen pt-24 pb-12">
        <div className="home-container px-4 sm:px-6">
          <section className={`${panelClass} max-w-2xl mx-auto text-center`}>
            <span className="section-kicker mb-3">REGISTRATION SUBMITTED</span>
            <h1 className={sectionTitleClass}>You're registered for {contest.title}!</h1>
            <p className={`mt-4 ${bodyTextClass}`}>
              Your registration has been received and is pending verification. You will be notified
              once it has been reviewed.
            </p>
            {successResult.registrationCode ? (
              <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5">
                <p className="text-xs font-bold uppercase tracking-[0.22em] home-accent">
                  Registration Code
                </p>
                <p className="mt-2 text-xl font-bold tracking-tight text-[var(--text)] sm:text-2xl">
                  {successResult.registrationCode}
                </p>
                <p className="mt-2 text-sm home-muted">
                  Keep this code for your records and any status checks.
                </p>
              </div>
            ) : null}
            <Link to="/contests" className={`${secondaryButtonClass} mt-6 inline-flex`}>
              Back to Contests
            </Link>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="home-shell min-h-screen pt-24 pb-12">
      <div className="home-container px-4 sm:px-6">
        <section className={`${panelClass} max-w-4xl mx-auto`}>
          <span className="section-kicker mb-3">CONTEST REGISTRATION</span>
          <h1 className={sectionTitleClass}>{contest.title}</h1>
          {contest.description ? (
            <p className={`mt-4 ${bodyTextClass}`}>{contest.description}</p>
          ) : null}

          <div className="mt-6 grid gap-3 text-xs home-muted sm:grid-cols-2 lg:grid-cols-3">
            {contest.venue ? <p>Venue: {contest.venue}</p> : null}
            {contestDate ? <p>Contest date: {contestDate}</p> : null}
            {deadline ? <p>Registration closes {deadline}</p> : null}
          </div>
        </section>

        {!contest.isRegistrationOpen ? (
          <section className={`${panelClass} max-w-4xl mx-auto text-center`}>
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-rose-600 dark:text-rose-400 sm:text-2xl">
                Registration Closed
              </h2>
              <p className="mt-3 text-sm home-muted sm:text-base">
                {contest.registrationClosedReason || "Registration is not currently open for this contest."}
              </p>
            </div>
            <Link to="/contests" className={`${secondaryButtonClass} mt-6 inline-flex`}>
              Back to Contests
            </Link>
          </section>
        ) : (
          <section className={`${panelClass} max-w-4xl mx-auto`}>
            <h2 className="text-xl font-bold text-[var(--text)] sm:text-2xl">
              {contest.type === "team" ? "Team Registration" : "Individual Registration"}
            </h2>
            <p className={`mt-2 mb-6 ${bodyTextClass}`}>
              {contest.type === "team"
                ? `This contest requires teams of ${contest.minTeamSize} to ${contest.maxTeamSize} members.`
                : "This contest is for individual entrants."}
            </p>
            {contest.requiresTshirtSize ? (
              <p className="mb-6 text-sm font-semibold text-[var(--brand)]">
                T-shirt size is required for every participant in this contest.
              </p>
            ) : null}

            {notice ? (
              <div className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-500">
                  {notice}
                </p>
              </div>
            ) : null}

            <form className="grid gap-6" onSubmit={handleSubmit}>
              {contest.type === "team" ? (
                <div className="grid gap-5 md:grid-cols-2">
                  <Input
                    label="Team Name"
                    value={teamName}
                    onChange={(event) => {
                      setTeamName(event.target.value);
                      if (errors.teamName) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.teamName;
                          return next;
                        });
                      }
                    }}
                    placeholder="Example: Algorithmic Avengers"
                    error={errors.teamName}
                  />
                  <Input
                    label="Reference (optional)"
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder="e.g. Facebook, a friend, poster, teacher"
                  />
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2">
                  <Input
                    label="Reference (optional)"
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder="e.g. Facebook, a friend, poster, teacher"
                  />
                </div>
              )}

              {errors.teamSize ? (
                <p className="text-xs font-semibold text-rose-500">{errors.teamSize}</p>
              ) : null}

              <div className="grid gap-6">
                {participants.map((participant, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--text)]">
                        {contest.type === "team"
                          ? index === 0
                            ? "Team Leader"
                            : `Member ${index + 1}`
                          : "Your Details"}
                      </p>
                      {contest.type === "team" && index > 0 && participants.length > contest.minTeamSize ? (
                        <button
                          type="button"
                          onClick={() => removeMember(index)}
                          className="text-xs font-semibold text-rose-500 hover:underline cursor-pointer"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                      <Input
                        label="Name"
                        value={participant.name}
                        onChange={(event) => updateParticipant(index, "name", event.target.value)}
                        placeholder="Full name"
                        error={errors[`p${index}-name`]}
                      />
                      <Input
                        label="Student ID"
                        value={participant.studentId}
                        onChange={(event) => updateParticipant(index, "studentId", event.target.value)}
                        placeholder="Student ID"
                        error={errors[`p${index}-studentId`]}
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={participant.email}
                        onChange={(event) => updateParticipant(index, "email", event.target.value)}
                        placeholder="name@example.com"
                        error={errors[`p${index}-email`]}
                      />
                      <Input
                        label="Phone"
                        value={participant.phone}
                        onChange={(event) => updateParticipant(index, "phone", event.target.value)}
                        placeholder="01XXXXXXXXX"
                        error={errors[`p${index}-phone`]}
                      />
                      <Input
                        label="Department"
                        value={participant.department}
                        onChange={(event) => updateParticipant(index, "department", event.target.value)}
                        placeholder="Example: CSE"
                        error={errors[`p${index}-department`]}
                      />
                      {contest.requiresTshirtSize ? (
                        <SelectInput
                          label="T-shirt Size"
                          value={participant.tshirtSize}
                          onChange={(event) => updateParticipant(index, "tshirtSize", event.target.value)}
                          error={errors[`p${index}-tshirtSize`]}
                          options={tshirtSizes}
                        />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {contest.type === "team" && participants.length < contest.maxTeamSize ? (
                <button
                  type="button"
                  onClick={addMember}
                  className={`${secondaryButtonClass} w-fit`}
                >
                  + Add Member
                </button>
              ) : null}

              {contest.requiresPayment ? (
                <div className="grid gap-5 rounded-2xl border border-[var(--border)] bg-[var(--surface-soft)] p-5">
                  <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--text)]">
                    Payment Information
                  </p>
                  {contest.paymentInstructions ? (
                    <p className="text-sm leading-relaxed home-muted whitespace-pre-line">
                      {contest.paymentInstructions}
                    </p>
                  ) : null}
                  <div className="grid gap-5 md:grid-cols-3">
                    <Input
                      label="Payment Method"
                      value={paymentMethod}
                      onChange={(event) => {
                        setPaymentMethod(event.target.value);
                        if (errors.paymentMethod) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.paymentMethod;
                            return next;
                          });
                        }
                      }}
                      placeholder="Example: bKash"
                      error={errors.paymentMethod}
                    />
                    <Input
                      label="Payment Sender Number"
                      value={paymentNumber}
                      onChange={(event) => {
                        setPaymentNumber(event.target.value);
                        if (errors.paymentNumber) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.paymentNumber;
                            return next;
                          });
                        }
                      }}
                      placeholder="The number used to send the payment"
                      error={errors.paymentNumber}
                    />
                    <Input
                      label="Transaction ID"
                      value={paymentTransactionId}
                      onChange={(event) => {
                        setPaymentTransactionId(event.target.value);
                        if (errors.paymentTransactionId) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.paymentTransactionId;
                            return next;
                          });
                        }
                      }}
                      placeholder="Enter the transaction ID"
                      error={errors.paymentTransactionId}
                    />
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col gap-4 border-t border-[var(--border)] pt-5 sm:flex-row sm:items-center sm:justify-end">
                <button type="submit" disabled={submitting} className={primaryButtonClass}>
                  {submitting ? "Submitting..." : "Submit Registration"}
                </button>
              </div>
            </form>
          </section>
        )}
      </div>
    </main>
  );
}

function Input({ label, error, ...props }) {
  return (
    <label className="grid gap-2.5">
      <span className="text-[0.75rem] font-bold uppercase tracking-[0.16em] text-[var(--text)]">
        {label}
      </span>
      <input
        className={`${inputClass} ${
          error ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20" : ""
        }`}
        {...props}
      />
      {error ? <span className="text-xs font-semibold text-rose-500">{error}</span> : null}
    </label>
  );
}

function SelectInput({ label, error, options, ...props }) {
  return (
    <label className="grid gap-2.5">
      <span className="text-[0.75rem] font-bold uppercase tracking-[0.16em] text-[var(--text)]">
        {label}
      </span>
      <select
        className={`${inputClass} ${
          error ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20" : ""
        }`}
        {...props}
      >
        <option value="">Select T-shirt size</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error ? <span className="text-xs font-semibold text-rose-500">{error}</span> : null}
    </label>
  );
}
